import { useCallback, useEffect, useRef, useState } from 'react';
import { CORRECT_FEEDBACK_AUDIO } from '../feedbackAudio';

// Feedback belongs to this screen and to a newly awarded server response only.
export default function useBookApprovalCelebration(enabled) {
  const [celebrations, setCelebrations] = useState({});
  const mounted = useRef(true);
  const timers = useRef(new Set());
  const audio = useRef(null);
  useEffect(() => {
    mounted.current = true;
    const pendingTimers = timers.current;
    return () => {
      mounted.current = false;
      pendingTimers.forEach(clearTimeout);
      pendingTimers.clear();
      audio.current?.pause();
      audio.current = null;
    };
  }, []);

  const prepare = useCallback(() => {
    if (!enabled || audio.current) return;
    try {
      // Construct during the teacher's gesture; never play on load or replay events.
      audio.current = new Audio(CORRECT_FEEDBACK_AUDIO);
      audio.current.playbackRate = .6;
    } catch (_) { /* Visual feedback also works without audio support. */ }
  }, [enabled]);

  const celebrate = useCallback((claim, result) => {
    if (!enabled || !mounted.current || result?.already_processed !== false || result?.awarded_points !== 5) return;
    setCelebrations((current) => ({ ...current, [claim.id]: claim }));
    try {
      if (audio.current) {
        audio.current.pause();
        audio.current.currentTime = 0;
        Promise.resolve(audio.current.play()).catch(() => {});
      }
    } catch (_) { /* A blocked/missing sound must not affect approval. */ }
    const timer = setTimeout(() => {
      timers.current.delete(timer);
      if (mounted.current) setCelebrations((current) => {
        const next = { ...current };
        delete next[claim.id];
        return next;
      });
    }, 1300);
    timers.current.add(timer);
  }, [enabled]);
  return { celebrations, prepare, celebrate, mounted };
}
